// The national counter, from the client's side.
//
// Every completed round adds one light to a shared total on the server. The
// network is optional: a round played offline is queued in prefs and flushed
// the next time anything asks. Nothing here throws — a counter that cannot be
// reached is shown as its last known value, or not at all.

import { prefs } from '../core/storage.js';
import { PUBLIC_ORIGIN } from '../../app-config.js';

export const MILESTONES = [100, 1000, 10000, 50000, 100000, 250000];

const MAX_PER_POST = 5;
const MAX_POSTS_PER_FLUSH = 3;

/** Same origin on the web; inside the native shell the page is `capacitor://`, so name the server. */
export function apiBase() {
  if (typeof window === 'undefined') return '';
  const native = window.Capacitor?.isNativePlatform?.() || window.location?.protocol === 'capacitor:';
  return native ? PUBLIC_ORIGIN : '';
}

async function request(method, body, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${apiBase()}/api/light`, {
      method,
      signal: ctrl.signal,
      cache: 'no-store',
      ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
    });
    let data = null;
    try { data = await res.json(); } catch { /* not JSON — treat as no data */ }
    return { status: res.status, data };
  } finally {
    clearTimeout(timer);
  }
}

function remember(total) {
  const entry = { total, fetchedAt: Date.now() };
  prefs.nationalCache = entry;
  return entry;
}

/** Last successfully seen total, or null. */
export function cachedTotal() {
  const c = prefs.nationalCache;
  return c && Number.isFinite(c.total) ? c : null;
}

/** GET the total. Returns { total, fetchedAt }; falls back to the cache, then null. */
export async function fetchTotal({ timeoutMs = 4000 } = {}) {
  try {
    const { status, data } = await request('GET', null, timeoutMs);
    if (status === 200 && Number.isFinite(data?.total)) return remember(data.total);
  } catch { /* offline, timed out, or blocked */ }
  return cachedTotal();
}

/** Add one light to the offline queue. Returns the queue length. */
export function queueLight() {
  const q = (Number(prefs.nationalQueue) || 0) + 1;
  prefs.nationalQueue = q;
  return q;
}

let flushing = null;

/**
 * Send queued lights, up to five per POST and three POSTs per call. A 429 or a
 * network failure keeps the queue for next time. Returns the latest total or
 * null. Concurrent callers share one flush so nothing is posted twice.
 */
export function flushQueue({ timeoutMs = 4000 } = {}) {
  if (flushing) return flushing;
  flushing = (async () => {
    let latest = cachedTotal()?.total ?? null;
    for (let i = 0; i < MAX_POSTS_PER_FLUSH; i += 1) {
      const queue = Number(prefs.nationalQueue) || 0;
      if (queue <= 0) break;
      const n = Math.min(queue, MAX_PER_POST);
      try {
        const { status, data } = await request('POST', { n }, timeoutMs);
        if (status === 200 && Number.isFinite(data?.total)) {
          prefs.nationalQueue = Math.max(0, (Number(prefs.nationalQueue) || 0) - n);
          latest = remember(data.total).total;
          continue;
        }
        if (Number.isFinite(data?.total)) latest = data.total;   // 429 still carries a number
        break;                                                    // 429, 4xx, 5xx: keep the queue
      } catch {
        break;                                                    // offline: keep the queue
      }
    }
    return latest;
  })().finally(() => { flushing = null; });
  return flushing;
}

/** A round finished: queue one light and try to send everything queued. */
export async function recordRound() {
  queueLight();
  const total = await flushQueue();
  return { total, queued: Number(prefs.nationalQueue) || 0 };
}

/** Highest milestone reached, the next one, and progress 0..1 toward it. */
export function milestoneFor(total) {
  const t = Math.max(0, Number(total) || 0);
  let reached = null;
  let next = null;
  for (const m of MILESTONES) {
    if (t >= m) reached = m;
    else if (next === null) next = m;
  }
  const from = reached ?? 0;
  const progress = next === null ? 1 : Math.min(1, Math.max(0, (t - from) / (next - from)));
  return { reached, next, progress };
}
